import { Link } from 'react-router-dom';
import {
  ArrowDown, BadgeCheck, BarChart3, LayoutDashboard, Mail, MapPin, ShieldCheck,
  Smartphone, Unlock, Users,
} from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FondoMapa from '@/components/landing/v3/FondoMapa';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import LogosMarquee from '@/components/landing/v3/LogosMarquee';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import VideoPresentacion from '@/components/landing/v3/VideoPresentacion';
import { withCta } from '@/lib/ctaTracking';
import { SALES_EMAIL } from '@/lib/salesContact';
import { pickLandingV3Content } from '@/pages/landingV3.content';
import { FOTO_EQUIPO, VIDEO_SOBRE_NOSOTROS } from '@/pages/landingV3.sobreNosotros';

/**
 * SobreNosotrosV3 — `/sobre-nosotros` y sus cuatro traducciones.
 *
 * La página institucional: quién hay detrás de MyTapstar. Cuenta el equipo,
 * Valencia y los dispositivos, en este orden de lectura:
 *
 *   hero → vídeo → de dónde venimos → lo que hacemos → cómo trabajamos →
 *   Valencia → cifras y marcas → cierre
 *
 * El vídeo va justo debajo del hero porque es lo primero que alguien que llega
 * aquí quiere ver: caras y voces, no más texto. Mientras no esté subido, el
 * hueco pinta un «muy pronto» (ver `VIDEO_SOBRE_NOSOTROS`).
 *
 * Todo el texto sale de `landingV3.sobreNosotros.js`; aquí solo hay maqueta.
 * Los datos de empresa (direcciones) son literales y no se traducen, igual que
 * en `LandingFooterV3`.
 */
const LOGO_LANZADERA = '/landing-images/v3/respaldo-lanzadera.png';

const ICONOS_HACEMOS = {
  dispositivos: Smartphone,
  plataforma: LayoutDashboard,
  equipo: Users,
};

const ICONOS_PRINCIPIOS = {
  reales: BadgeCheck,
  medible: BarChart3,
  libertad: Unlock,
  datos: ShieldCheck,
};

export default function SobreNosotrosV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.sobreNosotros;

  return (
    <LandingShellV3>
      <Hero c={c.hero} />
      <Video c={c.video} />
      <Historia c={c.historia} />
      <Hacemos c={c.hacemos} />
      <Principios c={c.principios} />
      <Valencia c={c.valencia} />
      <Cifras c={raiz.home.cifras} />
      <CtaFinalOscuro
        tituloA={c.cierre.tituloA}
        acento={c.cierre.acento}
        cta={raiz.home.cierre.cta}
        microcopy={raiz.home.cierre.microcopy}
        enterpriseA={raiz.home.cierre.enterpriseA}
        enterpriseCta={raiz.home.cierre.enterpriseCta}
        ctaTrial="sobre.cierre.trial"
        ctaVentas="sobre.cierre.ventas"
      />
    </LandingShellV3>
  );
}

function Hero({ c }) {
  return (
    <section className="bg-white px-6 pt-10 pb-10 md:pb-14">
      <div className="max-w-[860px] mx-auto text-center">
        <span className="inline-flex items-center gap-2 bg-v3-goldsoft text-v3-golddeep rounded-full px-3.5 py-[7px] text-[13px] font-semibold">
          <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
          {c.kicker}
        </span>

        <h1 className="text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.06] tracking-[-.035em] font-bold mt-6 text-v3-ink">
          {c.tituloA}
          <span className="text-v3-gold">{c.acento}</span>
          {c.tituloB}
        </h1>

        <p className="text-[17px] lg:text-[19px] leading-[1.6] text-v3-body mt-6 mx-auto max-w-[60ch]">
          {c.subtitulo}
        </p>

        <div className="flex gap-3 mt-8 flex-wrap justify-center">
          <CtaLink
            as={Link}
            cta="sobre.hero.trial"
            to={withCta('/register', 'sobre.hero.trial')}
            className="inline-flex items-center bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
          >
            {c.ctaPrimario}
          </CtaLink>
          {/* Ancla dentro de la propia página: navegación, no captación. */}
          <a
            href="#video"
            className="inline-flex items-center gap-2 bg-v3-sunken hover:bg-v3-track text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
          >
            {c.ctaSecundario}
            <ArrowDown className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Video({ c }) {
  const v = VIDEO_SOBRE_NOSOTROS;
  return (
    // `scroll-mt` para que el ancla del hero no deje el vídeo debajo de la
    // cabecera fija.
    <section id="video" className="px-6 pb-16 md:pb-[76px] scroll-mt-28">
      <div className="max-w-[1040px] mx-auto">
        <VideoPresentacion
          src={v.src}
          poster={v.poster}
          subtitulos={v.subtitulos}
          titulo={c.titulo}
          proximamente={c.proximamente}
          reproducir={c.reproducir}
        />
        <div className="mt-5 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6">
          <h2 className="text-[17px] font-bold tracking-[-.01em] text-v3-ink">{c.titulo}</h2>
          <p className="text-[14.5px] leading-[1.55] text-v3-muted sm:text-right max-w-[60ch]">{c.desc}</p>
        </div>
      </div>
    </section>
  );
}

function Historia({ c }) {
  return (
    <section className="bg-v3-surface px-6 py-16 md:py-[88px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>
        <div className="grid gap-10 lg:gap-16 mt-8 lg:grid-cols-[5fr_7fr] items-start">
          <div className="lg:sticky lg:top-28">
            <h2 className="text-[30px] md:text-[40px] leading-[1.1] tracking-[-.03em] font-bold text-v3-ink">
              {c.titulo}
            </h2>
            <blockquote className="mt-6 border-l-[3px] border-v3-gold pl-5 text-[18px] md:text-[20px] leading-[1.5] font-semibold tracking-[-.01em] text-v3-ink">
              {c.destacado}
            </blockquote>
          </div>
          <div className="space-y-5">
            {c.parrafos.map((p) => (
              <p key={p.slice(0, 32)} className="text-[16.5px] md:text-[17.5px] leading-[1.7] text-v3-body">
                {p}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Hacemos({ c }) {
  return (
    <section className="max-w-[1180px] mx-auto px-6 pt-16 md:pt-[88px] pb-8">
      <SectionKicker>{c.kicker}</SectionKicker>
      <h2 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink mt-6 max-w-[24ch]">
        {c.titulo}
      </h2>

      <div className="grid gap-5 mt-9 md:grid-cols-3">
        {c.items.map((it, i) => {
          const Icono = ICONOS_HACEMOS[it.clave];
          return (
            <article
              key={it.clave}
              className="relative bg-white border border-v3-line rounded-[20px] p-[26px] md:p-[30px] flex flex-col gap-4 v3-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl bg-v3-goldsoft flex items-center justify-center">
                  <Icono className="w-5 h-5 text-v3-golddeep" aria-hidden="true" />
                </div>
                <span className="text-[13px] font-bold text-v3-faint tabular-nums" aria-hidden="true">
                  0{i + 1}
                </span>
              </div>
              <h3 className="text-[19px] font-bold tracking-[-.02em] leading-[1.3] text-v3-ink">{it.titulo}</h3>
              <p className="text-[15px] leading-[1.6] text-v3-body">{it.desc}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Principios({ c }) {
  return (
    <section className="max-w-[1180px] mx-auto px-6 pt-16 md:pt-[76px] pb-16 md:pb-[76px]">
      <SectionKicker>{c.kicker}</SectionKicker>
      <h2 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink mt-6 max-w-[24ch]">
        {c.titulo}
      </h2>

      <div className="grid gap-x-10 gap-y-9 mt-10 sm:grid-cols-2 lg:grid-cols-4">
        {c.items.map((it) => {
          const Icono = ICONOS_PRINCIPIOS[it.clave];
          return (
            <div key={it.clave} className="border-t-2 border-v3-ink pt-5">
              <Icono className="w-6 h-6 text-v3-golddeep" aria-hidden="true" />
              <h3 className="text-[18px] font-bold tracking-[-.02em] leading-[1.3] text-v3-ink mt-4">{it.titulo}</h3>
              <p className="text-[15px] leading-[1.6] text-v3-body mt-2">{it.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Valencia({ c }) {
  return (
    <section className="px-6 pb-16 md:pb-[76px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-[28px] overflow-hidden grid lg:grid-cols-2">
        <div className="p-7 sm:p-10 md:p-12">
          <SectionKicker>{c.kicker}</SectionKicker>
          <h2 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink mt-6">
            {c.titulo}
          </h2>
          <p className="text-[16.5px] leading-[1.65] text-v3-body mt-5">{c.desc}</p>

          {/* Las mismas dos direcciones que el pie: datos de empresa, no copy. */}
          <dl className="grid gap-5 sm:grid-cols-2 mt-8">
            <div>
              <dt className="text-[12.5px] font-bold uppercase tracking-[.06em] text-v3-muted">{c.oficina}</dt>
              <dd className="text-[15px] leading-[1.55] text-v3-ink mt-1.5">
                Avenida Primado Reig 129, Entresuelo D
                <br />
                46020 Valencia
              </dd>
            </div>
            <div>
              <dt className="text-[12.5px] font-bold uppercase tracking-[.06em] text-v3-muted">{c.sede}</dt>
              <dd className="text-[15px] leading-[1.55] text-v3-ink mt-1.5">
                Lanzadera, Sc Puerto, 6
                <br />
                46024 València
              </dd>
            </div>
          </dl>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-5 mt-9">
            <div className="flex items-center gap-3">
              <span className="text-[13px] font-semibold text-v3-muted">{c.respaldo}</span>
              <img src={LOGO_LANZADERA} alt={c.altLanzadera} className="h-[22px] w-auto block" loading="lazy" />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[14.5px] text-v3-body">{c.contacto}</span>
              <a
                href={`mailto:${SALES_EMAIL}`}
                className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-v3-ink border-b border-v3-ink/30 hover:border-v3-ink transition-colors"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                {c.contactoCta}
              </a>
            </div>
          </div>
        </div>

        <div className="relative min-h-[280px] lg:min-h-full">
          {FOTO_EQUIPO ? (
            <img
              src={FOTO_EQUIPO}
              alt={c.fotoAlt}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <>
              <div className="absolute inset-0" aria-hidden="true">
                <FondoMapa className="w-full h-full" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                <div className="flex flex-col items-center">
                  <span className="bg-white rounded-full px-4 py-2 text-[14px] font-bold text-v3-ink v3-shadow">
                    Valencia
                  </span>
                  <span className="w-px h-4 bg-v3-ink/40" />
                  <span className="relative flex w-5 h-5">
                    <span className="absolute inset-0 rounded-full bg-v3-gold/50 animate-ping motion-reduce:animate-none" />
                    <span className="relative w-5 h-5 rounded-full bg-v3-gold border-[3px] border-white shadow" />
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Cifras({ c }) {
  return (
    <section className="max-w-[1180px] mx-auto px-6 pb-16 md:pb-[72px]">
      <div className="grid gap-5 items-center md:grid-cols-3">
        <h2 className="text-[24px] md:text-[26px] leading-[1.2] tracking-[-.02em] font-bold max-w-[26ch] text-v3-ink">
          {c.titulo}
        </h2>
        {c.items.map((x) => (
          <div key={x.valor} className="bg-v3-surface rounded-[20px] p-[30px]">
            <div className="text-[36px] md:text-[42px] font-bold tracking-[-.03em] leading-none text-v3-golddeep">
              {x.valor}
            </div>
            <div className="text-[15px] text-v3-body mt-2.5 leading-[1.5]">{x.etiqueta}</div>
          </div>
        ))}
      </div>

      <div className="mt-11">
        <LogosMarquee titulo={c.logosTitulo} />
      </div>
    </section>
  );
}
